"use client"

import { useState, useEffect, useRef, useMemo, memo } from "react"
import {
  Loader2,
  Check,
  ShieldCheck,
  Globe2,
  KeyRound,
} from "lucide-react"
import type { ScenarioSkill } from "@/lib/demo-scenarios/scenario-skills"
import { cn } from "@/lib/utils"

const PRIVACY_ICON: Record<string, typeof ShieldCheck> = {
  "local": ShieldCheck,
  "local-optional": ShieldCheck,
  "external": Globe2,
  "auth-required": KeyRound,
  "security-tool": ShieldCheck,
}

const PRIVACY_ORB_STYLE: Record<string, string> = {
  "local":          "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  "local-optional": "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  "external":       "bg-sky-500/15 text-sky-600 dark:text-sky-400",
  "auth-required":  "bg-sky-500/15 text-sky-600 dark:text-sky-400",
  "security-tool":  "bg-blue-500/15 text-blue-600 dark:text-blue-400",
}

function isLocalSkill(s: ScenarioSkill) {
  return s.privacy === "local" || s.privacy === "local-optional" || s.privacy === "security-tool"
}

type LoaderPhase = "loading-local" | "searching-community" | "loading-community" | "done"

/* ── Skills Loader — local-first → community search → done ── */
export const SkillsLoader = memo(function SkillsLoader({ skills, onComplete }: { skills: ScenarioSkill[]; onComplete?: () => void }) {
  const localSkills = useMemo(() => skills.filter(isLocalSkill), [skills])
  const communitySkills = useMemo(() => skills.filter(s => !isLocalSkill(s)), [skills])

  const [phase, setPhase] = useState<LoaderPhase>("loading-local")
  const [localLoadedCount, setLocalLoadedCount] = useState(0)
  const [communityLoadedCount, setCommunityLoadedCount] = useState(0)
  const [currentName, setCurrentName] = useState("")
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = []
    let t = 0

    // ── Phase 1: Load local skills one by one ──
    localSkills.forEach((skill, i) => {
      t += 180
      const delay = t
      timers.push(setTimeout(() => {
        setLocalLoadedCount(i + 1)
        setCurrentName(skill.name)
      }, delay))
    })

    // Brief pause after local skills loaded
    t += 600
    const afterLocalDelay = t
    timers.push(setTimeout(() => {
      if (communitySkills.length > 0) {
        setPhase("searching-community")
        setCurrentName("")
      } else {
        setPhase("done")
        onCompleteRef.current?.()
      }
    }, afterLocalDelay))

    // ── Phase 2: Search community (pause for effect) ──
    if (communitySkills.length > 0) {
      t += 1200
      const startCommunityDelay = t
      timers.push(setTimeout(() => {
        setPhase("loading-community")
      }, startCommunityDelay))

      // ── Phase 3: Load community skills one by one ──
      communitySkills.forEach((skill, i) => {
        t += 200
        const delay = t
        timers.push(setTimeout(() => {
          setCommunityLoadedCount(i + 1)
          setCurrentName(skill.name)
        }, delay))
      })

      // ── Phase 4: Done ──
      t += 400
      timers.push(setTimeout(() => {
        setPhase("done")
        setCurrentName("")
        onCompleteRef.current?.()
      }, t))
    }

    return () => timers.forEach(clearTimeout)
  }, [localSkills, communitySkills])

  const allLoaded = [...localSkills.slice(0, localLoadedCount), ...communitySkills.slice(0, communityLoadedCount)]

  // Phase label (stable, no animation)
  const phaseLabel = phase === "loading-local"
    ? "加载本地 Skill"
    : phase === "searching-community"
      ? "正在搜索来自社区的 Skills..."
      : phase === "loading-community"
        ? "加载社区 Skill"
        : ""

  // ── Done phase — summary with local/community counts ──
  if (phase === "done") {
    return (
      <div className="animate-in fade-in duration-300 rounded-xl px-3.5 py-2">
        <div className="flex items-center gap-2">
          <div className="flex -space-x-1">
            {skills.map((s, i) => {
              const Icon = PRIVACY_ICON[s.privacy] ?? ShieldCheck
              return (
                <div key={i} className={cn(
                  "h-4 w-4 rounded-full ring-[1.5px] ring-background flex items-center justify-center",
                  PRIVACY_ORB_STYLE[s.privacy] ?? "bg-muted text-muted-foreground"
                )}>
                  <Icon className="h-2 w-2" />
                </div>
              )
            })}
          </div>
          <span className="text-caption text-foreground/70">
            已就绪 {skills.length} 个 Skills
          </span>
          <Check className="h-3 w-3 text-violet-500 dark:text-violet-400 animate-in zoom-in duration-200" />
          <span className="text-micro text-muted-foreground ml-0.5">
            {localSkills.length} 本地{communitySkills.length > 0 && ` · ${communitySkills.length} 社区`}
          </span>
        </div>
      </div>
    )
  }

  // ── Loading / Searching phases ──
  const isCommunityPhase = phase === "searching-community" || phase === "loading-community"

  return (
    <div className="animate-in fade-in duration-200 rounded-xl px-3.5 py-2.5">
      <div className="flex items-center gap-2.5">
        {/* Loaded orbs */}
        <div className="flex -space-x-1">
          {allLoaded.map((s, i) => {
            const Icon = PRIVACY_ICON[s.privacy] ?? ShieldCheck
            return (
              <div key={i} className={cn(
                "h-4 w-4 rounded-full ring-[1.5px] ring-background flex items-center justify-center animate-in zoom-in duration-150",
                PRIVACY_ORB_STYLE[s.privacy] ?? "bg-muted text-muted-foreground"
              )}>
                <Icon className="h-2 w-2" />
              </div>
            )
          })}
        </div>

        {/* Spinner */}
        <Loader2 className={cn(
          "h-3 w-3 animate-spin shrink-0",
          phase === "searching-community" ? "text-sky-500 dark:text-sky-400" : "text-violet-500 dark:text-violet-400"
        )} />

        {/* Status: stable label + flickering skill name */}
        <div className="flex-1 min-w-0 flex items-center gap-1">
          <span className={cn(
            "text-xs shrink-0",
            isCommunityPhase ? "text-sky-600 dark:text-sky-400" : "text-foreground/70"
          )}>
            {phaseLabel}
          </span>
          {currentName && (phase === "loading-local" || phase === "loading-community") && (
            <span
              key={currentName}
              className="text-xs text-muted-foreground truncate animate-in fade-in duration-100"
            >
              {currentName}
            </span>
          )}
        </div>

        {/* Counter */}
        <span className="text-micro text-muted-foreground tabular-nums shrink-0">
          {allLoaded.length}/{skills.length}
        </span>
      </div>
    </div>
  )
})
